import React, { useMemo } from 'react';
import { Alert, Box, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { DEEP_GREEN, MUTED, WARN_ORANGE } from '../colorTokens';
import { KIND_LABEL, OUTCOME_COLORS, formatDate, formatDay, formatMinutes, formatNumber, formatPercent } from './format';
import { useWaves } from './useReleaseCadence';
import { MeasureRow, ScoreCard } from './ScoreParts';
import { NoRolloutNotice, VersionPicker, useVersionPicker } from './VersionPicker';

const GROWTH_FLAG = 5;
const GROUP_LABEL = { internal: 'Internal', preprod: 'Pre-prod', standard: 'Standard', vip: 'VIP', unknown: 'Unknown' };
const OUTCOMES = ['success', 'failed', 'skipped', 'other'];

const OutcomeBar =({ group }) => {
  const total = group.runs || 1;
  return (
    <Tooltip title={OUTCOMES.map((o) => `${o}: ${formatNumber(group[o])}`).join(' · ')} placement="top">
      <Box sx={{ display: 'flex', height: 12, width: 160, borderRadius: '6px', overflow: 'hidden', bgcolor: 'rgba(0,0,0,0.06)' }}>
        {OUTCOMES.map((o) => (group[o] ? <Box key={o} sx={{ width: `${(group[o] / total) * 100}%`, bgcolor: OUTCOME_COLORS[o] }} /> : null))}
      </Box>
    </Tooltip>
  );
};

const dailyTotals = (groups) => {
  const byDay = new Map();
  for (const g of groups) {
    for (const d of g.daily) {
      const row = byDay.get(d.day) || { day: d.day, success: 0, failed: 0, skipped: 0, other: 0 };
      OUTCOMES.forEach((o) => { row[o] += d[o]; });
      byDay.set(d.day, row);
    }
  }
  return [...byDay.values()].sort((a, b) => a.day.localeCompare(b.day));
};

const VersionGlance = ({ release }) => {
  if (!release || !release.vsPrevious) return null;
  const { vsPrevious } = release;
  const head = vsPrevious.version
    ? `${release.version} against the previous ${KIND_LABEL[release.kind].toLowerCase()} rollout, ${vsPrevious.version}`
    : `${release.version} · first ${KIND_LABEL[release.kind].toLowerCase()} rollout on record, so there is nothing earlier to compare with`;
  return (
    <ScoreCard
      headline={(
        <Box sx={{ display: 'grid', gap: 1.5 }}>
          <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>{head}</Typography>
          <MeasureRow measures={vsPrevious.measures} baselineLabel={vsPrevious.version} />
          {release.startsAtHistoryEdge && <Typography variant="caption" sx={{ color: MUTED }}>This rollout began before the recorded history, so its figures may be understated.</Typography>}
        </Box>
      )}
    />
  );
};

const WavesView = ({ overview, onOpenPlans }) => {
  const { entries, entry, select, loading: catalogLoading, rolloutVersion } = useVersionPicker(overview);
  const { data, loading, error } = useWaves(rolloutVersion);
  const groups = data && rolloutVersion ? data.groups : [];
  const daily = useMemo(() => dailyTotals(groups), [groups]);
  const release = overview.releases.find((r) => r.version === rolloutVersion);

  if (!entries.length) return <Typography sx={{ color: MUTED }}>No deployment runs are recorded yet.</Typography>;

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <VersionPicker entries={entries} selected={entry ? entry.version : ''} onChange={select} loading={loading || catalogLoading} />
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      {entry && !entry.hasRollout && <NoRolloutNotice entry={entry} historyStart={overview.history.earliest} onOpenPlans={onOpenPlans} />}
      <VersionGlance release={release} />

      {groups.length > 0 && (
        <>
          <Paper variant="outlined" sx={{ borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)', overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F8F9FA', '& th': { color: DEEP_GREEN, fontWeight: 600, borderBottom: '2px solid rgba(26,46,34,0.15)' } }}>
                  <TableCell>Wave</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell align="right">Instances</TableCell>
                  <TableCell>Growth vs previous wave</TableCell>
                  <TableCell>Outcome of runs</TableCell>
                  <TableCell align="right">Failure rate</TableCell>
                  <TableCell align="right">Recovered</TableCell>
                  <TableCell align="right">Still failing</TableCell>
                  <TableCell align="right">Median / p90 run</TableCell>
                  <TableCell align="right">Gap from previous</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((g) => {
                  const big = g.sizeRatioToPrevious != null && g.sizeRatioToPrevious >= GROWTH_FLAG;
                  return (
                    <TableRow key={g.group} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{GROUP_LABEL[g.group] || g.group}</TableCell>
                      <TableCell>{formatDay(g.start)} – {formatDay(g.end)}</TableCell>
                      <TableCell align="right">{formatNumber(g.instances)}</TableCell>
                      <TableCell sx={{ color: big ? WARN_ORANGE : 'inherit', fontWeight: big ? 600 : 400 }}>
                        {g.sizeRatioToPrevious == null ? '–' : g.sizeRatioToPrevious === 0 ? 'Under 0.1× the previous wave' : `${g.sizeRatioToPrevious}× the previous wave`}
                      </TableCell>
                      <TableCell><OutcomeBar group={g} /></TableCell>
                      <TableCell align="right">{formatPercent(g.failureRate)}</TableCell>
                      <TableCell align="right">{formatNumber(g.instancesRecovered)}</TableCell>
                      <TableCell align="right">{formatNumber(g.instancesStillFailing)}</TableCell>
                      <TableCell align="right">{formatMinutes(g.medianMinutes)} / {formatMinutes(g.p90Minutes)}</TableCell>
                      <TableCell align="right">{g.gapDaysFromPrevious == null ? '–' : g.gapDaysFromPrevious < 0 ? 'Overlaps' : `${g.gapDaysFromPrevious} d`}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' }}>
            <Typography sx={{ fontWeight: 600, color: DEEP_GREEN, mb: 1 }}>Runs per day · {entry.version}</Typography>
            <Box sx={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={(d) => formatDay(d)} tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                  <ChartTooltip labelFormatter={(d) => formatDate(d)} />
                  <Legend />
                  {OUTCOMES.map((o) => (
                    <Bar key={o} dataKey={o} stackId="runs" fill={OUTCOME_COLORS[o]} name={o} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default WavesView;
